import { getSupabaseClient } from "@/lib/supabase/client";
import type { KarmaGender, KarmaPreferredGender, KarmaUser } from "@/lib/karma/types";
import { parseBirthDate } from "@/lib/karma/numerology";
import { getSessionUser } from "@/lib/supabase/auth";

type ProfileSeed = Pick<KarmaUser, "handle" | "displayName"> & {
  gender?: KarmaGender;
  prefPartnerGender?: KarmaPreferredGender;
};

function isGenericDisplayName(value: string) {
  const normalized = value
    .toLocaleLowerCase("tr-TR")
    .replace(/\s+/g, " ")
    .trim();

  return !normalized || ["kullanıcı", "kullanici", "user", "biri", "üye", "uye"].includes(normalized);
}

function pickDisplayNameFromMetadata(meta: Record<string, unknown>) {
  const first = typeof meta.first_name === "string" ? meta.first_name.trim() : "";
  const last = typeof meta.last_name === "string" ? meta.last_name.trim() : "";
  const full = `${first} ${last}`.trim();
  if (full) return full;

  const fullName = typeof meta.full_name === "string" ? meta.full_name.trim() : "";
  if (fullName) return fullName;

  const name = typeof meta.name === "string" ? meta.name.trim() : "";
  if (name) return name;

  const displayName = typeof meta.display_name === "string" ? meta.display_name.trim() : "";
  if (displayName) return displayName;

  return "";
}

function normalizeGender(raw: unknown): KarmaGender | undefined {
  if (raw === "kadin" || raw === "erkek" || raw === "non_binary" || raw === "belirtmek_istemiyorum") return raw;
  return undefined;
}

function normalizePreferredGender(raw: unknown): KarmaPreferredGender | undefined {
  if (raw === "kadin" || raw === "erkek" || raw === "non_binary" || raw === "herkes") return raw;
  return undefined;
}

function isFallbackDisplayName(args: { value: string; emailHandle: string; handles: string[] }) {
  const normalized = args.value
    .toLocaleLowerCase("tr-TR")
    .replace(/\s+/g, " ")
    .trim();

  if (isGenericDisplayName(args.value)) return true;
  if (args.emailHandle && normalized === args.emailHandle.toLocaleLowerCase("tr-TR")) return true;

  const normalizedHandles = args.handles.map((h) => h.toLocaleLowerCase("tr-TR").trim()).filter(Boolean);
  if (normalizedHandles.includes(normalized)) return true;

  return normalized.startsWith("user_");
}

export async function upsertMyProfile(me: ProfileSeed) {
  const supabase = getSupabaseClient();

  // Avoid supabase.auth.getSession() here; read from storage-first logic.
  const sessionUser = await getSessionUser();
  if (!sessionUser?.id) throw new Error("Oturum yok");

  const user = {
    id: sessionUser.id,
    email: sessionUser.email,
  };

  const meta = (sessionUser.metadata ?? {}) as Record<string, unknown>;
  const metadataGender = normalizeGender(meta.gender);
  const metadataPreferredGender = normalizePreferredGender(meta.pref_partner_gender ?? meta.preferred_gender);
  const birthDay = Number(meta.birth_day ?? 0);
  const birthMonth = Number(meta.birth_month ?? 0);
  const birthYear = Number(meta.birth_year ?? 0);

  const birthDateIso = (() => {
    if (!Number.isFinite(birthDay) || !Number.isFinite(birthMonth) || !Number.isFinite(birthYear)) return null;
    if (!birthDay || !birthMonth || !birthYear) return null;
    const parsed = parseBirthDate({ day: birthDay, month: birthMonth, year: birthYear });
    const yyyy = String(parsed.year).padStart(4, "0");
    const mm = String(parsed.month).padStart(2, "0");
    const dd = String(parsed.day).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  })();

  const emailHandle = user.email ? user.email.split("@")[0].trim() : "";

  const cleanHandle = String(me.handle ?? "").trim();
  const baseHandle = cleanHandle || emailHandle || `user_${user.id.slice(0, 8)}`;
  const handleCandidates = Array.from(
    new Set([baseHandle, `${baseHandle}_${user.id.slice(0, 4)}`, `${baseHandle}_${user.id.slice(0, 8)}`].filter(Boolean)),
  );

  const metadataDisplayName = pickDisplayNameFromMetadata(meta);
  const seededDisplayName = String(me.displayName ?? "").trim();
  const preferredDisplayName = metadataDisplayName || seededDisplayName || emailHandle || "Kullanıcı";

  const cleanDisplayName = String(preferredDisplayName ?? "").trim();
  const displayName = cleanDisplayName || (emailHandle || "Kullanıcı");

  const existingProfile = await supabase.from("karma_profiles").select("birth_date, display_name, handle").eq("id", user.id).maybeSingle();
  const existingBirthDate = String(existingProfile.data?.birth_date ?? "").trim();
  const existingDisplayName = String(existingProfile.data?.display_name ?? "").trim();
  const existingHandle = String(existingProfile.data?.handle ?? "").trim();

  const shouldBackfillBirthDate = Boolean(!existingBirthDate && birthDateIso);
  const shouldRepairDisplayName = isFallbackDisplayName({
    value: existingDisplayName,
    emailHandle,
    handles: [baseHandle, existingHandle],
  });

  const shouldSetDisplayName = Boolean(!existingDisplayName || shouldRepairDisplayName);
  const shouldSetHandle = Boolean(!existingHandle);

  const upsertHandles = shouldSetHandle ? handleCandidates : [existingHandle];

  let lastError: unknown = null;

  for (const handle of upsertHandles) {
    const payload: Record<string, unknown> = {
      id: user.id,
      handle,
      match_enabled: true,
    };

    const resolvedGender = normalizeGender(me.gender) ?? metadataGender;
    const resolvedPreferredGender = normalizePreferredGender(me.prefPartnerGender) ?? metadataPreferredGender;

    if (shouldSetDisplayName) {
      payload.display_name = displayName;
    }

    if (shouldBackfillBirthDate) {
      payload.birth_date = birthDateIso;
    }

    if (resolvedGender) {
      payload.gender = resolvedGender;
    }

    if (resolvedPreferredGender) {
      payload.preferred_gender = resolvedPreferredGender;
    }

    let { error } = await supabase.from("karma_profiles").upsert(payload as any, { onConflict: "id" });

    const missingGenderColumn =
      error &&
      String((error as any)?.message ?? "").includes("column") &&
      (String((error as any)?.message ?? "").includes("gender") || String((error as any)?.message ?? "").includes("preferred_gender"));

    if (missingGenderColumn) {
      const fallbackPayload = { ...payload } as Record<string, unknown>;
      delete fallbackPayload.gender;
      delete fallbackPayload.preferred_gender;
      const retry = await supabase.from("karma_profiles").upsert(fallbackPayload as any, { onConflict: "id" });
      error = retry.error;
    }

    lastError = error;
    if (!error) return;
  }

  if (lastError) throw lastError;
}
